import React, { useEffect, useRef, useState } from 'react';
import http, { admin as adminApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, Save, Play, Pause, Megaphone, Sparkles, RefreshCw, Upload, Trash2, FileAudio, CheckCircle2 } from 'lucide-react';

const VOICE_OPTIONS = [
  { key: 'fable',   label: 'Fable',   note: 'Storyteller · warm · expressive' },
  { key: 'onyx',    label: 'Onyx',    note: 'Deep · authoritative · grounded' },
  { key: 'nova',    label: 'Nova',    note: 'Bright · energetic · upbeat' },
  { key: 'coral',   label: 'Coral',   note: 'Warm · friendly · conversational' },
];

const humanBytes = (n) => {
  if (!n) return '0 B';
  const units = ['B','KB','MB','GB'];
  let i = 0, v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

/**
 * Admin panel — edit the PSA heading + body, pick the narration voice, and
 * preview each voice A/B before committing. Saving flushes the disk cache
 * implicitly because the audio URL is content-hashed on the server.
 */
export default function AdminPsaPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [psa, setPsa] = useState(null);
  const [heading, setHeading] = useState('');
  const [body, setBody] = useState('');
  const [voice, setVoice] = useState('fable');

  // Voice preview state — one <audio> shared across all voice buttons.
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState('');       // which voice key is playing
  const [loadingVoice, setLoadingVoice] = useState('');
  const blobUrls = useRef({});                      // {voice: blobUrl}

  useEffect(() => {
    let alive = true;
    http.get('/psa').then(r => {
      if (!alive) return;
      setPsa(r.data);
      setHeading(r.data.heading || '');
      setBody(r.data.isPlaceholder ? '' : (r.data.body || ''));
      setVoice(r.data.voice || 'fable');
    }).catch(e => {
      toast({ title: 'Could not load PSA', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    }).finally(() => { if (alive) setLoading(false); });
    return () => {
      alive = false;
      // Revoke any object URLs we minted at unmount
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const urls = blobUrls.current;
      Object.values(urls).forEach(u => { try { URL.revokeObjectURL(u); } catch {} });
    };
  }, []);

  // ---- Uploaded audio (replaces TTS when present) --------------------
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const pickFile = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';   // reset so choosing the same file twice still fires change
    if (!f) return;
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!['mp3', 'wav', 'm4a', 'aac'].includes(ext) && !f.type?.includes('audio')) {
      toast({ title: 'Unsupported file', description: 'Please pick an MP3, WAV, M4A, or AAC file.', variant: 'destructive' });
      return;
    }
    if (f.size > 30 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max 30 MB — try compressing the audio.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    setUploadPct(0);
    try {
      const updated = await adminApi.uploadPsaAudio(f, setUploadPct);
      setPsa(updated);
      toast({ title: 'Audio uploaded', description: 'This file will now play on the PSA page.' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || String(err), variant: 'destructive' });
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const removeUpload = async () => {
    if (!window.confirm('Remove the uploaded audio? The PSA page will fall back to the TTS narration.')) return;
    setUploading(true);
    try {
      const updated = await adminApi.deletePsaAudio();
      setPsa(updated);
      toast({ title: 'Uploaded audio removed', description: 'TTS narration is live again.' });
    } catch (err) {
      toast({ title: 'Remove failed', description: err?.response?.data?.detail || String(err), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const previewVoice = async (v) => {
    const el = audioRef.current;
    if (!el) return;
    // Toggle pause if the same voice is playing
    if (playing === v) {
      el.pause();
      setPlaying('');
      return;
    }
    try {
      setLoadingVoice(v);
      if (!blobUrls.current[v]) {
        blobUrls.current[v] = await adminApi.psaVoicePreviewBlobUrl(v);
      }
      el.src = blobUrls.current[v];
      await el.play();
      setPlaying(v);
    } catch (e) {
      toast({ title: `Preview failed (${v})`, description: e?.response?.data?.detail || String(e), variant: 'destructive' });
      setPlaying('');
    } finally {
      setLoadingVoice('');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      if (heading.trim() && heading.trim() !== psa?.heading) patch.heading = heading.trim();
      if (body.trim() && body.trim() !== psa?.body) patch.body = body.trim();
      if (voice && voice !== psa?.voice) patch.voice = voice;
      if (Object.keys(patch).length === 0) {
        toast({ title: 'Nothing to save', description: 'No changes detected.' });
        return;
      }
      const updated = await adminApi.updatePsa(patch);
      setPsa(updated);
      setHeading(updated.heading || '');
      setBody(updated.isPlaceholder ? '' : (updated.body || ''));
      setVoice(updated.voice || 'fable');
      toast({ title: 'PSA updated', description: 'Fresh narration is being generated on the next play.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (!psa) return;
    setHeading(psa.heading || '');
    setBody(psa.isPlaceholder ? '' : (psa.body || ''));
    setVoice(psa.voice || 'fable');
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-400" data-testid="admin-psa-loading">
        <Loader2 className="animate-spin" size={16}/> Loading PSA…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-psa-panel">
      <div className="pbb-card p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-semibold flex items-center gap-2"><Megaphone size={16} className="text-amber-400"/> Public Service Announcement</div>
            <p className="text-xs text-gray-400 mt-1">Edit the copy or swap the narration voice. Saving invalidates the cached audio automatically — the next visitor triggers fresh generation.</p>
          </div>
          {psa?.isPlaceholder && (
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-full px-2 py-0.5">
              <Sparkles size={11}/> Placeholder copy live
            </span>
          )}
        </div>
      </div>

      <div className="pbb-card p-5 space-y-4">
        <div>
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Heading</label>
          <input
            data-testid="admin-psa-heading"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Body</label>
          <textarea
            data-testid="admin-psa-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            placeholder="Paste the final polished PSA script here…"
            className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm leading-relaxed"
          />
          <div className="mt-1 text-[11px] text-gray-500">{body.length}/4000 chars</div>
        </div>
      </div>

      <div className="pbb-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-semibold text-sm flex items-center gap-2"><FileAudio size={14} className="text-amber-400"/> Narration audio file</div>
            <p className="text-xs text-gray-400 mt-1">
              Upload an MP3, WAV, M4A, or AAC file to replace the AI-generated narration.
              {' '}
              <span className="inline-flex items-center gap-0.5">
                <span className="text-white font-semibold">MP3</span>
                <span className="inline-block ml-1 mr-1.5 px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wider rounded-full bg-green-500/15 border border-green-400/50 text-green-300"
                       data-testid="admin-psa-rec-mp3">Recommended</span>
              </span>
              and
              <span className="inline-flex items-center gap-0.5">
                <span className="text-white font-semibold ml-1">WAV</span>
                <span className="inline-block ml-1 mr-0.5 px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wider rounded-full bg-green-500/15 border border-green-400/50 text-green-300"
                       data-testid="admin-psa-rec-wav">Recommended</span>
              </span>
              play back most reliably across devices. Any uploaded file always wins over the TTS voice below.
            </p>
          </div>
          {psa?.hasUploadedAudio && (
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-green-300 bg-green-500/10 border border-green-500/40 rounded-full px-2 py-0.5">
              <CheckCircle2 size={11}/> Uploaded file is live
            </span>
          )}
        </div>

        {psa?.hasUploadedAudio && psa?.uploadedAudio ? (
          <div className="rounded-xl border border-[#1f1f1f] bg-black p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-semibold truncate">{psa.uploadedAudio.originalFilename || `psa.${psa.uploadedAudio.ext}`}</div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {psa.uploadedAudio.ext?.toUpperCase()} · {humanBytes(psa.uploadedAudio.sizeBytes)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={pickFile}
                  disabled={uploading}
                  data-testid="admin-psa-audio-replace"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-[#2a2a2a] text-xs font-semibold disabled:opacity-60"
                >
                  <Upload size={12}/> Replace
                </button>
                <button
                  type="button"
                  onClick={removeUpload}
                  disabled={uploading}
                  data-testid="admin-psa-audio-remove"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/10 hover:bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-semibold disabled:opacity-60"
                >
                  <Trash2 size={12}/> Remove
                </button>
              </div>
            </div>
            <audio
              src={psa.audioUrl?.startsWith('http') ? psa.audioUrl : `${process.env.REACT_APP_BACKEND_URL}${psa.audioUrl}`}
              controls
              preload="metadata"
              className="w-full"
              data-testid="admin-psa-audio-preview"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={pickFile}
            disabled={uploading}
            data-testid="admin-psa-audio-upload"
            className="w-full rounded-xl border border-dashed border-[#2a2a2a] hover:border-amber-400/60 bg-black hover:bg-black/70 p-6 flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-60"
          >
            <div className="w-10 h-10 rounded-full bg-amber-400/10 border border-amber-400/40 flex items-center justify-center">
              <Upload size={16} className="text-amber-300"/>
            </div>
            <div className="mt-2 font-semibold text-sm inline-flex items-center gap-1 flex-wrap justify-center">
              <span>Upload</span>
              <span>MP3</span>
              <span className="px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wider rounded-full bg-green-500/15 border border-green-400/50 text-green-300">Recommended</span>
              <span>,</span>
              <span>WAV</span>
              <span className="px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wider rounded-full bg-green-500/15 border border-green-400/50 text-green-300">Recommended</span>
              <span>, M4A, or AAC</span>
            </div>
            <div className="text-[11px] text-gray-400">Max 30 MB · replaces the TTS narration on /psa</div>
          </button>
        )}

        {uploading && (
          <div data-testid="admin-psa-audio-progress" className="text-xs text-gray-400 flex items-center gap-2">
            <Loader2 size={12} className="animate-spin"/>
            Uploading… {uploadPct ? `${uploadPct}%` : ''}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/aac,audio/x-aac,.mp3,.wav,.m4a,.aac"
          onChange={handleFile}
          className="hidden"
          data-testid="admin-psa-audio-input"
        />
      </div>

      <div className="pbb-card p-5 space-y-4">
        <div>
          <div className="font-semibold text-sm">Narration voice · A/B preview</div>
          <p className="text-xs text-gray-400 mt-1">
            {psa?.hasUploadedAudio
              ? 'The voice below is only used when no audio file is uploaded. Remove the uploaded audio to fall back to this voice.'
              : 'Tap a voice to hear a short sample line. The selected voice is used for the live PSA narration.'}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {VOICE_OPTIONS.map((v) => {
            const isSelected = voice === v.key;
            const isPlaying = playing === v.key;
            const isLoading = loadingVoice === v.key;
            return (
              <div
                key={v.key}
                data-testid={`admin-psa-voice-${v.key}`}
                className={`rounded-xl border p-4 transition-colors ${isSelected ? 'border-green-500 bg-green-500/5' : 'border-[#1f1f1f] bg-black hover:border-[#2a2a2a]'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">{v.label}</div>
                    <div className="text-[11px] text-gray-400">{v.note}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => previewVoice(v.key)}
                    disabled={isLoading}
                    data-testid={`admin-psa-voice-${v.key}-preview`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-xs font-semibold disabled:opacity-60"
                    aria-label={isPlaying ? `Pause ${v.label} preview` : `Preview ${v.label} voice`}
                  >
                    {isLoading ? <Loader2 size={12} className="animate-spin"/> : isPlaying ? <Pause size={12}/> : <Play size={12}/>}
                    {isLoading ? 'Loading' : isPlaying ? 'Pause' : 'Preview'}
                  </button>
                </div>
                <div className="mt-3">
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="radio"
                      name="psa-voice"
                      value={v.key}
                      checked={isSelected}
                      onChange={() => setVoice(v.key)}
                      data-testid={`admin-psa-voice-${v.key}-select`}
                      className="accent-green-500"
                    />
                    <span className={isSelected ? 'text-green-400 font-semibold' : 'text-gray-300'}>
                      {isSelected ? 'Selected for live PSA' : 'Use this voice'}
                    </span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
        <audio
          ref={audioRef}
          onEnded={() => setPlaying('')}
          onPause={() => setPlaying('')}
          preload="none"
        />
      </div>

      <div className="flex items-center gap-2 flex-wrap sticky bottom-2 z-10">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-psa-save"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-black text-sm font-bold disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save PSA
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={saving}
          data-testid="admin-psa-reset"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 hover:border-neutral-500 text-xs disabled:opacity-60"
        >
          <RefreshCw size={12}/> Reset
        </button>
        <a
          href="/psa"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="admin-psa-open-live"
          className="ml-auto text-xs text-gray-400 hover:text-white underline"
        >
          Open live /psa page →
        </a>
      </div>
    </div>
  );
}
